import { scopeProjectRef } from "@amoreg/client-runtime/environment";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LinkIcon, PlusIcon, RotateCcwIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { sortScopedProjectsForSidebar } from "../components/Sidebar.logic";
import { Button } from "../components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../components/ui/empty";
import { SidebarInset } from "../components/ui/sidebar";
import { useAuthGateState } from "../hooks/useAuthGateState";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import {
  useAllEnvironmentShellsBootstrapped,
  useProjects,
  useThreadShells,
} from "../state/entities";
import { useEnvironments } from "../state/environments";
import { orgUrlSegment } from "../orgRouting";
import { resolveDefaultOrg, useOrgs } from "../state/orgs";
import { APP_DISPLAY_NAME } from "~/branding";
import { hasCloudPublicConfig } from "~/cloud/publicConfig";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";

function ChatIndexRouteView() {
  const authGateState = useAuthGateState();
  const { environments } = useEnvironments();

  if (!authGateState) {
    return null;
  }

  if (authGateState.status === "hosted-static" && environments.length === 0) {
    return <HostedStaticOnboardingState />;
  }

  return <OrgRedirectLanding />;
}

/**
 * `/` is a redirector, not a page: it lands on the last-picked organization
 * (`/$org`, the org slug) — the DB's `lastChosenOrgId` first, then the local
 * record, then the first org the user may act in. Servers without a tenant
 * directory (plain T3 dev) fall back to the legacy most-recent-project draft
 * landing.
 */
function OrgRedirectLanding() {
  const navigate = useNavigate();
  const { orgs, available, isPending, error, refresh } = useOrgs();
  const defaultOrg = useMemo(() => resolveDefaultOrg(orgs), [orgs]);

  useEffect(() => {
    if (defaultOrg) {
      void navigate({ to: "/$org", params: { org: orgUrlSegment(defaultOrg.id) }, replace: true });
    }
  }, [defaultOrg, navigate]);

  if (isPending || defaultOrg !== null) {
    return null;
  }
  if (error !== null) {
    return (
      <IndexHero
        title="Couldn’t load your organizations"
        description="The server didn’t answer. Retry in a moment."
        action={
          <Button size="sm" onClick={refresh}>
            <RotateCcwIcon className="size-4" />
            Try again
          </Button>
        }
      />
    );
  }
  if (!available) {
    return <LegacyDraftLanding />;
  }
  return (
    <IndexHero
      title="No organizations yet"
      description="Your account isn’t a member of any organization. Ask an administrator to invite you."
    />
  );
}

/**
 * Fallback for servers with no tenant directory: drop into a draft for the
 * most recently active project, exactly as the pre-tenant index did.
 */
function LegacyDraftLanding() {
  const projects = useProjects();
  const threads = useThreadShells();
  const bootstrapped = useAllEnvironmentShellsBootstrapped();
  const handleNewThread = useNewThreadHandler();
  const startingRef = useRef(false);
  const [startState, setStartState] = useState({ failed: false, retryRequest: 0 });

  const mostRecentProject = useMemo(
    () =>
      bootstrapped
        ? (sortScopedProjectsForSidebar(projects, threads, "updated_at")[0] ?? null)
        : null,
    [bootstrapped, projects, threads],
  );

  useEffect(() => {
    if (mostRecentProject === null || startingRef.current) {
      return;
    }
    startingRef.current = true;
    void handleNewThread(scopeProjectRef(mostRecentProject.environmentId, mostRecentProject.id), {
      replace: true,
    }).catch(() => {
      startingRef.current = false;
      setStartState((state) => ({ ...state, failed: true }));
    });
  }, [handleNewThread, mostRecentProject, startState.retryRequest]);

  if (!bootstrapped) {
    return null;
  }
  if (mostRecentProject !== null) {
    return startState.failed ? (
      <IndexHero
        title="Couldn’t start a new thread"
        description="The workspace is still available. Try opening the draft again."
        action={
          <Button
            size="sm"
            onClick={() =>
              setStartState((state) => ({ failed: false, retryRequest: state.retryRequest + 1 }))
            }
          >
            <RotateCcwIcon className="size-4" />
            Try again
          </Button>
        }
      />
    ) : null;
  }
  return (
    <IndexHero
      title="What should we work on?"
      description="No workspaces are available on this server yet."
    />
  );
}

function IndexHero({
  title,
  description,
  action,
}: {
  readonly title: string;
  readonly description: string;
  readonly action?: React.ReactNode;
}) {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <Empty className="flex-1">
        <EmptyHeader className="max-w-md">
          <EmptyTitle className="text-foreground text-xl">{title}</EmptyTitle>
          <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
            {description}
          </EmptyDescription>
          {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
        </EmptyHeader>
      </Empty>
    </SidebarInset>
  );
}

export const Route = createFileRoute("/_chat/")({
  component: ChatIndexRouteView,
});

function HostedStaticOnboardingState() {
  const cloudEnabled = hasCloudPublicConfig();

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <header
          className={cn(
            "border-b border-border px-3 py-2 transition-[padding-left] duration-(--sidebar-motion-duration) ease-(--sidebar-motion-ease) motion-reduce:transition-none sm:px-5 sm:py-3",
            COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground md:text-muted-foreground/60">
              {APP_DISPLAY_NAME}
            </span>
          </div>
        </header>

        <Empty className="flex-1">
          <div className="w-full max-w-xl rounded-3xl border border-border/55 bg-card/20 px-8 py-12 shadow-sm/5">
            <EmptyHeader className="max-w-none">
              <div className="mx-auto mb-5 flex size-11 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground">
                <LinkIcon className="size-5" />
              </div>
              <EmptyTitle className="text-foreground text-xl">
                Connect an environment to get started
              </EmptyTitle>
              <EmptyDescription className="mt-2 text-sm leading-relaxed text-muted-foreground/78">
                {cloudEnabled
                  ? "Sign in to T3 Connect to connect a linked environment through its managed tunnel, or add a reachable backend manually."
                  : "Add a reachable backend manually to start working from this browser."}
              </EmptyDescription>
            </EmptyHeader>
          </div>
        </Empty>
      </div>
    </SidebarInset>
  );
}
